import { FC, useEffect } from 'react';
import dayjs from 'dayjs';
import { Trans } from 'src/components/trans';
import { useTranslation } from 'src/hooks/use-translation';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import LoadingButton from '@mui/lab/LoadingButton';

import { useAuthContext } from 'src/auth/hooks';
import { Image } from 'src/components/image';
import { useSnackbar } from 'src/components/snackbar';
import { UserAvatar } from 'src/components/user-avatar';
import { useBoolean } from 'src/hooks/use-boolean';
import { INVITATION_STATUS, InvitationDetails } from 'src/types';
import { formatDate, parseDate } from 'src/utils/format-date';

import StudentFaviconOutlineSvg from 'icons/colored/student-favicon-outline.svg';

import { useAcceptInvitation, useInvitationDetails, usePendingInvitation } from './hooks';
import { InviteError } from './types';

const NAMESPACE = 'dashboard/invitation-accept';

const ACTIONS_SX = {
  flexDirection: { xs: 'column-reverse', sm: 'row' },
  gap: 1.5,
} as const;

// ----------------------------------------------------------------------

type DialogProps = {
  invitation: InvitationDetails;
  onResolved: () => void;
};

const InviteDialog: FC<DialogProps> = ({ invitation, onResolved }) => {
  const { t } = useTranslation(NAMESPACE);
  const { user } = useAuthContext();
  const { value: open, onFalse: closeDialog } = useBoolean(true);
  const { accept, isAccepting, confirmError } = useAcceptInvitation(invitation);

  const { platform, inviter, expires_at: expiresAt, invited_email: invitedEmail } = invitation;

  const inviterName = `${inviter.first_name} ${inviter.last_name}`.trim();
  const expiryDate = parseDate(expiresAt);
  const isExpired = invitation.status === INVITATION_STATUS.EXPIRED || expiryDate.isBefore(dayjs());
  const daysLeft = Math.max(0, expiryDate.diff(dayjs(), 'day'));
  const isWrongAccount = !!user?.email && invitedEmail.toLowerCase() !== user.email.toLowerCase();

  // Everything except `generic` is knowable before the mentor clicks Accept, so
  // the dialog opens already explaining itself instead of failing on submit.
  const derivedError: InviteError | null = (() => {
    if (invitation.status === INVITATION_STATUS.ACCEPTED) return 'already-accepted';
    if (isExpired) return 'expired';
    if (isWrongAccount) return 'wrong-account';
    return null;
  })();

  const error = confirmError ?? derivedError;
  const acceptDisabled = !!error && error !== 'generic';

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown
      onClose={(_event, reason) => {
        if (reason === 'backdropClick') return;
      }}
      slotProps={{ transition: { onExited: onResolved } }}
      aria-labelledby="invitation-accept-title"
      sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}
    >
      <DialogContent sx={{ p: { xs: 3, sm: 5 }, pb: 3 }}>
        <Stack spacing={3} alignItems="center" sx={{ textAlign: 'center' }}>
          <Box
            component={platform.icon ? Image : StudentFaviconOutlineSvg}
            width={72}
            height={72}
            src={platform.icon ?? undefined}
            alt={platform.name}
            sx={{ width: 72, height: 72, color: 'text.secondary' }}
          />

          <Stack spacing={0.5} alignItems="center">
            <Typography variant="h4" id="invitation-accept-title">
              {platform.name}
            </Typography>
            <Typography variant="body1" sx={{ color: 'text.secondary' }}>
              <Trans
                ns={NAMESPACE}
                i18nKey="invited-as"
                values={{ role: invitation.role_name }}
                components={{ b: <b /> }}
              />
            </Typography>
          </Stack>

          <Stack
            direction="row"
            alignItems="center"
            spacing={1.5}
            sx={{ width: 1, py: 1.25, px: 2, borderRadius: 1.5, bgcolor: 'background.neutral' }}
          >
            <UserAvatar
              name={inviterName}
              image={inviter.image ?? undefined}
              size={36}
              isAvatarOnly
            />
            <Stack sx={{ minWidth: 0, textAlign: 'left' }}>
              <Typography variant="subtitle2" noWrap>
                {inviterName}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {t('sent-on', { date: formatDate(invitation.created_at) })}
              </Typography>
            </Stack>
            <Typography
              variant="caption"
              sx={{
                color: isExpired ? 'error.main' : 'warning.main',
                fontWeight: 600,
                ml: 'auto',
                flexShrink: 0,
                whiteSpace: 'nowrap',
              }}
            >
              {isExpired ? t('expired-badge') : t('days-left', { count: daysLeft })}
            </Typography>
          </Stack>

          {error === 'expired' && (
            <Alert severity="error" sx={{ width: 1, textAlign: 'left' }}>
              {t('errors.expired', { date: formatDate(expiresAt), inviter: inviterName })}
            </Alert>
          )}
          {error === 'already-accepted' && (
            <Alert severity="error" sx={{ width: 1, textAlign: 'left' }}>
              {t('errors.already-accepted', { platform: platform.name })}
            </Alert>
          )}
          {error === 'wrong-account' && (
            <Alert severity="error" sx={{ width: 1, textAlign: 'left' }}>
              <Trans
                ns={NAMESPACE}
                i18nKey="errors.wrong-account"
                values={{ email: invitedEmail }}
                components={{ b: <b /> }}
              />
            </Alert>
          )}
          {error === 'generic' && (
            <Alert severity="error" sx={{ width: 1, textAlign: 'left' }}>
              {t('errors.generic')}
            </Alert>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={ACTIONS_SX}>
        <Button
          variant="outlined"
          color="inherit"
          onClick={closeDialog}
          sx={{ width: { xs: 1, sm: 'auto' }, m: { xs: '0!important', sm: undefined } }}
        >
          {t('decline')}
        </Button>
        <LoadingButton
          color="primary"
          variant="contained"
          loading={isAccepting}
          disabled={acceptDisabled}
          onClick={() => accept(closeDialog)}
          sx={{ width: { xs: 1, sm: 'auto' }, m: { xs: '0!important', sm: undefined } }}
        >
          {t('accept')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

// ----------------------------------------------------------------------

const InviteDialogSkeleton: FC = () => {
  const { t } = useTranslation(NAMESPACE);

  return (
    <Dialog
      open
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown
      slotProps={{
        paper: { 'aria-label': t('loading'), 'aria-labelledby': undefined, 'aria-busy': true },
      }}
    >
      <DialogContent sx={{ p: { xs: 3, sm: 5 }, pb: 3 }}>
        <Stack spacing={3} alignItems="center">
          <Skeleton variant="rounded" width={72} height={72} />

          <Stack spacing={0.5} alignItems="center" sx={{ width: 1 }}>
            <Skeleton variant="text" width={200} height={36} />
            <Skeleton variant="text" width={260} />
          </Stack>

          <Skeleton variant="rounded" width="100%" height={64} sx={{ borderRadius: 1.5 }} />
        </Stack>
      </DialogContent>

      <DialogActions sx={ACTIONS_SX}>
        <Skeleton
          variant="rounded"
          height={36}
          sx={{ width: { xs: 1, sm: 100 }, m: { xs: '0!important', sm: undefined } }}
        />
        <Skeleton
          variant="rounded"
          height={36}
          sx={{ width: { xs: 1, sm: 160 }, m: { xs: '0!important', sm: undefined } }}
        />
      </DialogActions>
    </Dialog>
  );
};

// ----------------------------------------------------------------------

export const InvitationAcceptDialog = () => {
  const { t } = useTranslation(NAMESPACE);
  const { showSnackbar } = useSnackbar();
  const { authenticated } = useAuthContext();
  const { invitationId, clearPendingInvitation } = usePendingInvitation();
  const pendingId = authenticated ? invitationId : null;
  const { invitation, isLoading, isError } = useInvitationDetails(pendingId);

  // An invite we cannot load has no content to build a dialog around — drop it
  // rather than showing an empty shell the mentor cannot act on.
  useEffect(() => {
    if (!isError) return;
    clearPendingInvitation();
    showSnackbar({ message: t('errors.not-found'), severity: 'error' });
  }, [isError, clearPendingInvitation, showSnackbar, t]);

  if (pendingId && isLoading) return <InviteDialogSkeleton />;

  if (!invitation) return null;

  // Remount per invitation so the dialog's open/error state never carries over.
  return (
    <InviteDialog key={invitation.id} invitation={invitation} onResolved={clearPendingInvitation} />
  );
};
